import { Container } from 'pixi.js';
import { INK, TRACKING, destroyWidget, richLabel, panel, uiPad, type Panel } from '../ui';
import type { TooltipBox } from './cardTooltip';
import { t } from '../../i18n';

/**
 * **塞回异常时的"功能提示"** —— 贴在**被拖那张 [异常] 的正下方**。
 *
 * 回答两个玩家真正关心的问题：
 *
 * - 现在松手，它会成为**接下来第几张**被抽到的牌？
 * - 若之后**没有人洗乱抽牌堆**，大概会轮到**谁**抽到它？
 *
 * 这是"塞回"这个动作唯一的反馈来源：绿落点线已经删掉（用户），牌又小、位置
 * 又靠指针，光看间距数不出第几张 —— 所以把结论直接写下来。
 *
 * 内容由 `analysis/insertInfo.ts` 的纯函数 `insertOutcome` 算出，这里只负责
 * 排版与贴边。
 */

/** 提示面板和牌之间的空隙（逻辑像素） */
export const INSERT_HINT_GAP = 10;
/** 离画布边缘至少留多少 */
export const INSERT_HINT_MARGIN = 10;

/**
 * **提示面板该摆在哪。** 纯函数（测试钉得住"贴边时不跑出画布"）。
 *
 * 规则：
 * 1. **在牌下方**（用户要求）；水平居中于牌；
 * 2. 下方越出画布就往上夹回画布内（面板比画布还高时贴底）；
 * 3. 水平也夹进左右边距。
 */
export function insertHintPlacement(
  box: TooltipBox,
  size: { readonly width: number; readonly height: number },
  bounds: { readonly width: number; readonly height: number },
  gap: number = INSERT_HINT_GAP,
  margin: number = INSERT_HINT_MARGIN,
): { readonly x: number; readonly y: number } {
  const below = box.y + box.height + gap;
  const maxY = Math.max(margin, bounds.height - size.height - margin);
  const y = Math.min(maxY, Math.max(margin, below));
  const centered = box.x + box.width / 2 - size.width / 2;
  const maxX = Math.max(margin, bounds.width - size.width - margin);
  const x = Math.min(maxX, Math.max(margin, centered));
  return { x, y };
}

export interface InsertHintOptions {
  /** UI 倍率 */
  readonly scale: number;
  /** 画布尺寸（夹边用） */
  readonly bounds: { readonly width: number; readonly height: number };
  /** 面板宽度（设计尺寸） */
  readonly width?: number;
}

export class InsertHint {
  /** 加进场景的东西，**要盖在牌堆之上** */
  readonly container: Container;

  readonly #scale: number;
  readonly #bounds: { width: number; height: number };
  readonly #width: number;

  #panel: Panel | null = null;
  /** 现在这段文字对应的内容签名 —— 不变就不重建 */
  #shown: string | null = null;
  #visible = false;

  constructor(options: InsertHintOptions) {
    this.#scale = options.scale;
    this.#bounds = { width: options.bounds.width, height: options.bounds.height };
    this.#width = options.width ?? 220;
    this.container = new Container();
    this.container.label = 'insert-hint-layer';
    this.container.eventMode = 'none';
    this.container.visible = false;
    this.container.zIndex = 10000;
  }

  get visible(): boolean {
    return this.#visible;
  }

  /** 现在显示的内容签名（诊断用）。没显示就是 null */
  get showing(): string | null {
    return this.#visible ? this.#shown : null;
  }

  /**
   * **显示 / 刷新。** `box` 是被拖那张 [异常] 在逻辑画布坐标里的包围盒。
   *
   * `drawsUntil`：它会被第几张抽到（1 = 下一张）。`drawerName`：预计抽到它的
   * 玩家名，未知就传 null。
   */
  show(box: TooltipBox, drawsUntil: number, drawerName: string | null): void {
    const signature = `${drawsUntil}|${drawerName ?? ''}`;
    if (this.#shown !== signature || this.#panel === null) this.#rebuild(drawsUntil, drawerName, signature);
    const panel_ = this.#panel;
    if (panel_ === null) return;
    const at = insertHintPlacement(
      box,
      { width: panel_.width, height: panel_.height },
      this.#bounds,
    );
    this.container.visible = true;
    this.#visible = true;
    this.container.position.set(at.x, at.y);
  }

  hide(): void {
    this.container.visible = false;
    this.#visible = false;
  }

  resize(bounds: { readonly width: number; readonly height: number }): void {
    this.#bounds.width = bounds.width;
    this.#bounds.height = bounds.height;
  }

  destroy(): void {
    if (this.#panel !== null) destroyWidget(this.#panel);
    this.#panel = null;
    this.container.destroy({ children: true });
  }

  #rebuild(drawsUntil: number, drawerName: string | null, signature: string): void {
    if (this.#panel !== null) destroyWidget(this.#panel);
    const panel_ = panel({
      scale: this.#scale,
      width: uiPad(this.#width, this.#scale),
      title: t('render.insertHint.title'),
    });
    const inner = uiPad(this.#width - 24, this.#scale);

    const first = richLabel({
      text: t('render.insertHint.drawsUntil', { n: drawsUntil }),
      scale: this.#scale,
      size: 12,
      fill: INK.text,
      role: 'cjk',
      wrapWidth: inner,
      tracking: TRACKING.normal,
    });
    panel_.body.addChild(first);

    const second = richLabel({
      text:
        drawerName === null
          ? t('render.insertHint.drawerUnknown')
          : t('render.insertHint.drawerNamed', { name: drawerName }),
      scale: this.#scale,
      size: 11,
      fill: INK.textDim,
      role: 'cjk',
      wrapWidth: inner,
    });
    second.position.set(0, first.height + uiPad(6, this.#scale));
    panel_.body.addChild(second);

    panel_.fitContent(second.y + second.height);
    this.#panel = panel_;
    this.#shown = signature;
    this.container.addChild(panel_.container);
  }
}
